// app/privacy/choices/page.tsx

/**
 * @fileoverview Privacy Choices page with Consent Mode v2 and Global Privacy Control support.
 * @description Provides users with granular control over analytics, functional cookies, advertising,
 * and California "Do Not Sell or Share" settings. Integrates with Google Tag Manager consent mode
 * and automatically respects Global Privacy Control signals.
 * 
 * Key Features:
 * - Consent Mode v2 integration with GTM
 * - Global Privacy Control (GPC) signal detection and automatic opt-out
 * - CPRA "Do Not Sell or Share" compliance
 * - Persistent consent preferences with localStorage
 * - Real-time consent updates to GTM dataLayer
 * - Export functionality for user data portability
 * 
 * @module privacy/choices
 */

'use client'

import { useEffect, useState, useCallback, useMemo, type ReactElement } from 'react'
import { logClientError, clientLogger } from '~/lib/logger-client'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Button } from '~/components/ui/Button'
import { Switch } from '~/components/ui/Switch'
import { Badge as ShadcnBadge } from '~/components/ui/ShadcnBadge'
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert'
import { ShieldCheck, Globe, Info, Cookie, X, Download, RotateCcw, Save } from 'lucide-react'
import type { ConsentPreferences } from '~/types/consent'
import {
  applyConsentModeFromPreferences,
  buildConsentPreferences,
  persistConsentPreferences,
  replayEdgeConsentCookiesFromStoredPreferences,
} from '~/lib/analytics/consent-preferences'
import {
  CONSENT_STORAGE_KEY,
  wasAnalyticsEffectivelyAllowed,
} from '~/lib/analytics/consent'
import { recordAttributionTouchAsync } from '~/lib/analytics/attribution'
import { isConsentRequiredRegionClient } from '~/lib/analytics/consent-context'
import {
  getHydratedConsentRequiredSignal,
  hydrateRegionCookieIfMissing,
} from '~/lib/analytics/hydrate-region-cookie'
import { pageview } from '~/lib/gtag'

// --- Constants for consent management ---
// Single source of truth for the storage key lives in ~/lib/analytics/consent;
// re-aliased here so a `_v2` bump can't silently desync this page from the gate
// and the Consent Mode replay snippet.
const CONSENT_KEY = CONSENT_STORAGE_KEY
const CONSENT_VERSION = 1
const SAVE_SUCCESS_DURATION = 2500

interface NavigatorWithGPC extends Navigator {
  globalPrivacyControl?: boolean
}

/**
 * Detects Global Privacy Control signal
 * @returns boolean indicating if GPC is active
 */
function detectGPC(): boolean {
  if (typeof navigator === 'undefined') return false
  return (navigator as NavigatorWithGPC).globalPrivacyControl === true
}

/**
 * Creates default consent preferences based on GPC signal
 * Uses conservative defaults when GPC is detected
 * 
 * @param gpc - Whether Global Privacy Control signal is detected
 * @returns Default consent preferences object
 */
function createDefaultPreferences(gpc: boolean): ConsentPreferences {
  return {
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
    strictlyNecessary: true as const, // Always required for core functionality
    analytics: true, // Allow by default (opt-out model)
    functional: true, // Generally safe for user experience
    ads: !gpc, // Only deny if GPC signal is present
    doNotSellOrShare: gpc, // Automatically opt-out if GPC detected
    gpcDetected: gpc,
  }
}

/**
 * Validates consent preferences structure
 * @param obj - Object to validate
 * @returns boolean indicating if object is valid ConsentPreferences
 */
function isValidConsentPreferences(obj: unknown): obj is ConsentPreferences {
  if (!obj || typeof obj !== 'object') return false
  
  const candidate = obj as Record<string, unknown>
  
  return (
    typeof candidate.version === 'number' &&
    typeof candidate.updatedAt === 'string' &&
    typeof candidate.strictlyNecessary === 'boolean' &&
    typeof candidate.analytics === 'boolean' &&
    typeof candidate.functional === 'boolean' &&
    typeof candidate.ads === 'boolean' &&
    typeof candidate.doNotSellOrShare === 'boolean' &&
    typeof candidate.gpcDetected === 'boolean'
  )
}

/**
 * Reads consent preferences from localStorage with error handling
 * Returns null if no preferences exist or parsing fails
 * 
 * @returns Parsed consent preferences or null
 */
function readConsentPreferences(): ConsentPreferences | null {
  if (typeof window === 'undefined') return null
  
  try {
    const raw = localStorage.getItem(CONSENT_KEY)
    if (!raw) return null
    
    const parsed: unknown = JSON.parse(raw)
    
    if (!isValidConsentPreferences(parsed)) {
      clientLogger.warn('[Privacy] Invalid consent preferences format, clearing storage', { component: 'privacy/choices' })
      localStorage.removeItem(CONSENT_KEY)
      return null
    }
    
    // Check if preferences are from current version
    if (parsed.version !== CONSENT_VERSION) {
      clientLogger.info('[Privacy] Consent preferences version mismatch, using defaults', { component: 'privacy/choices' })
      return null
    }
    
    return parsed
  } catch (error) {
    logClientError(error, { component: 'privacy/choices', context: 'Failed to parse consent preferences' })
    localStorage.removeItem(CONSENT_KEY) // Clear corrupted data
    return null
  }
}

/**
 * Creates region-aware default preferences for first visit / reset.
 *
 * @param gpc - Whether Global Privacy Control signal is detected.
 * @returns Default consent preferences object.
 */
function createInitialPreferences(gpc: boolean): ConsentPreferences {
  // Opt-in-required defaults only when region is confirmed EEA/UK/CH — not when
  // geo is unknown or hydration failed (rest-of-world opt-out applies there).
  if (isConsentRequiredRegionClient() || getHydratedConsentRequiredSignal()) {
    return buildConsentPreferences(false)
  }
  return createDefaultPreferences(gpc)
}

/**
 * Replays attribution + pageview when analytics is newly granted from Manage.
 */
function replayTrackingAfterAnalyticsGrant(): void {
  if (typeof window === 'undefined') return
  void recordAttributionTouchAsync(window.location.pathname).then(() => {
    const query = window.location.search || ''
    pageview(`${window.location.pathname}${query}`)
  })
}

/**
 * Privacy Choices page component
 * Provides comprehensive privacy controls with GPC support and GTM integration
 * 
 * @returns Privacy choices interface or null during hydration
 */
export default function PrivacyChoicesPage(): ReactElement | null {
  // Component state for GPC detection and consent preferences
  const [hasGPC, setHasGPC] = useState<boolean>(false)
  const [prefs, setPrefs] = useState<ConsentPreferences | null>(null)
  const [saved, setSaved] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Initialize component state and consent preferences
  useEffect(() => {
    const initializePreferences = async (): Promise<void> => {
      await hydrateRegionCookieIfMissing()
      // Detect Global Privacy Control signal
      const gpcDetected = detectGPC()
      setHasGPC(gpcDetected)

      // Load existing preferences or create defaults
      const existingPrefs = readConsentPreferences()
      const initialPrefs = existingPrefs ?? createInitialPreferences(gpcDetected)

      setPrefs(initialPrefs)
      setIsLoading(false)

      // Only replay a REAL stored choice into Consent Mode on load. We must NOT
      // push the opt-out-model defaults (analytics/ads granted) here, or merely
      // viewing this page would queue a granted `consent update` that undoes the
      // region-scoped denied default for an EEA/UK/CH visitor with no stored
      // choice. The head bootstrap's region default stands until the user saves.
      if (existingPrefs) {
        replayEdgeConsentCookiesFromStoredPreferences()
        applyConsentModeFromPreferences(existingPrefs)
      }
    }

    void initializePreferences()
  }, [])

  // Memoize whether advertising controls should be locked due to GPC or user choice
  const lockAds = useMemo(() => {
    return prefs ? (prefs.gpcDetected || prefs.doNotSellOrShare) : false
  }, [prefs])

  /**
   * Creates a toggle handler for specific consent preference
   * Updates state and marks preferences as unsaved
   * 
   * @param key - The consent preference key to toggle
   * @returns Function that handles the toggle with new value
   */
  const createToggleHandler = useCallback((key: keyof ConsentPreferences): ((value: boolean) => void) => {
    return (value: boolean): void => {
      if (!prefs) return
      
      const updatedPrefs = { 
        ...prefs, 
        [key]: value, 
        updatedAt: new Date().toISOString() 
      }
      setPrefs(updatedPrefs)
      setSaved(false) // Mark as unsaved to show save button state
    }
  }, [prefs])

  /**
   * Saves current preferences to localStorage and applies to GTM
   * Shows temporary success message to user
   */
  const handleSave = useCallback((): void => {
    if (!prefs) return
    
    
    const updatedPrefs = { 
      ...prefs, 
      updatedAt: new Date().toISOString() 
    }

    const analyticsNewlyGranted =
      updatedPrefs.analytics && !wasAnalyticsEffectivelyAllowed()
    
    persistConsentPreferences(updatedPrefs)
    if (analyticsNewlyGranted) {
      replayTrackingAfterAnalyticsGrant()
    }
    setPrefs(updatedPrefs)
    
    // Show success feedback
    setSaved(true)
    setTimeout(() => setSaved(false), SAVE_SUCCESS_DURATION)
  }, [prefs])

  /**
   * Resets preferences to defaults based on current GPC state
   * Immediately saves and applies the reset preferences
   */
  const handleReset = useCallback((): void => {
    const gpcDetected = detectGPC()
    const resetPrefs = createInitialPreferences(gpcDetected)
    
    // Update state and persist immediately
    setPrefs(resetPrefs)
    persistConsentPreferences(resetPrefs)
    
    // Show success feedback
    setSaved(true)
    setTimeout(() => setSaved(false), SAVE_SUCCESS_DURATION)
  }, [])

  /**
   * Exports current preferences as downloadable JSON file
   * Provides data portability for user privacy compliance
   */
  const handleExport = useCallback((): void => {
    if (!prefs) return
    
    try {
      const exportData = {
        ...prefs,
        exportedAt: new Date().toISOString(),
        userAgent: navigator.userAgent,
        gpcSupported: 'globalPrivacyControl' in navigator
      }
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
        type: 'application/json' 
      })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      
      link.href = url
      link.download = `adsights-privacy-choices-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // Clean up object URL to prevent memory leaks
      URL.revokeObjectURL(url)
    } catch (error) {
      logClientError(error, { component: 'privacy/choices', context: 'Failed to export preferences' })
    }
  }, [prefs])

  // Show loading state during initialization
  if (isLoading || !prefs) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-10">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-8"></div>
          <div className="space-y-4">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-10">
      {/* Page header with description */}
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight">Privacy Choices</h1>
        <p className="text-muted-foreground mt-2">
          Control analytics, functional cookies, advertising, and your California &quot;Do Not Sell or Share&quot; setting. 
          We honor the Global Privacy Control (GPC) signal when present.
        </p>
      </div>

      {/* GPC detection alert */}
      {hasGPC && (
        <Alert className="mb-6 border-amber-300 bg-amber-50">
          <Globe className="h-4 w-4" />
          <AlertTitle>Global Privacy Control detected</AlertTitle>
          <AlertDescription>
            A GPC signal is active in your browser. We have applied your opt-out preferences automatically 
            (ads disabled; Do Not Sell/Share = on). You can change other settings below.
          </AlertDescription>
        </Alert>
      )}

      {/* Consent preference cards */}
      <div className="grid grid-cols-1 gap-6">
        {/* Strictly Necessary - Always enabled */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-green-600"/> 
              Strictly Necessary
            </CardTitle>
            <ShadcnBadge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              Always On
            </ShadcnBadge>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Required for core site/app functionality and security (e.g., session, load balancing). 
            These cannot be disabled.
          </CardContent>
        </Card>

        {/* Analytics consent */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2">
              <Cookie className="h-5 w-5 text-blue-600"/> 
              Analytics
            </CardTitle>
            <Switch 
              checked={prefs.analytics} 
              onCheckedChange={createToggleHandler('analytics')} 
            />
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Helps us understand usage to improve the product (e.g., GA4/PostHog/Mixpanel). 
            When off, we minimize data collection per Consent Mode.
          </CardContent>
        </Card>

        {/* Functional consent */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-purple-600"/> 
              Functional
            </CardTitle>
            <Switch 
              checked={prefs.functional} 
              onCheckedChange={createToggleHandler('functional')} 
            />
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Improves experience (e.g., remembering preferences, chat widgets). 
            Disabling may reduce certain conveniences.
          </CardContent>
        </Card>

        {/* Advertising consent - can be locked by GPC or user choice */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-orange-600"/> 
              Advertising
            </CardTitle>
            <Switch 
              checked={prefs.ads && !lockAds} 
              onCheckedChange={createToggleHandler('ads')} 
              disabled={lockAds} 
            />
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Enables advertising-related storage and personalization signals. 
            Disabled automatically if Global Privacy Control is detected or if you opt out of &quot;Sell/Share.&quot;
          </CardContent>
        </Card>

        {/* CPRA "Do Not Sell or Share" consent */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2">
              <X className="h-5 w-5 text-red-600"/> 
              Do Not Sell or Share (CPRA)
            </CardTitle>
            <Switch 
              checked={prefs.doNotSellOrShare} 
              onCheckedChange={createToggleHandler('doNotSellOrShare')} 
            />
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Turning this on opts you out of cross-context behavioral advertising and other &quot;sale/share&quot; 
            activities under California law. We also honor the Global Privacy Control signal automatically.
          </CardContent>
        </Card>
      </div>

      {/* Action buttons */}
      <div className="mt-8 flex flex-wrap gap-3">
        <Button 
          onClick={handleSave} 
          disabled={saved}
          className="flex items-center gap-2"
        >
          <Save className="h-4 w-4" />
          {saved ? 'Saved!' : 'Save preferences'}
        </Button>
        <Button 
          variant="secondary" 
          onClick={handleReset}
          className="flex items-center gap-2"
        >
          <RotateCcw className="h-4 w-4" />
          Reset defaults
        </Button>
        <Button 
          variant="outline" 
          onClick={handleExport}
          className="flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Export preferences
        </Button>
      </div>

      {/* Success message */}
      {saved && (
        <Alert className="mt-4 border-green-300 bg-green-50">
          <ShieldCheck className="h-4 w-4" />
          <AlertDescription className="text-green-800">
            Preferences saved and applied successfully.
          </AlertDescription>
        </Alert>
      )}

      {/* Additional privacy information and links */}
      <div className="mt-10 space-y-4 text-sm text-muted-foreground">
        <p>
          Use the controls above for cookie and California opt-out preferences. For access,
          deletion, correction, or portability requests, email{' '}
          <a className="underline hover:text-foreground transition-colors" href="mailto:privacy@adsights.ai">
            privacy@adsights.ai
          </a>{' '}
          (see our{' '}
          <a className="underline hover:text-foreground transition-colors" href="/privacy">
            Privacy Policy
          </a>
          , Appendix A).
        </p>
        <p>
          For industry opt-outs, visit the Digital Advertising Alliance:{' '}
          <a 
            className="underline hover:text-foreground transition-colors" 
            href="https://optout.aboutads.info" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            US
          </a> ·{' '}
          <a 
            className="underline hover:text-foreground transition-colors" 
            href="https://youradchoices.ca/choices/" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            Canada
          </a>.
        </p>
      </div>
    </div>
  )
}

// --- _app-level setup reminders (add outside this file) ---
// 1) In <head>, load GTM with a dataLayer and default denied consent:
// <script>
//   window.dataLayer = window.dataLayer || [];
//   window.dataLayer.push(['consent', 'default', {
//     'analytics_storage': 'denied',
//     'ad_storage': 'denied',
//     'ad_user_data': 'denied',
//     'ad_personalization': 'denied',
//     'functionality_storage': 'granted',
//     'security_storage': 'granted'
//   }]);
// </script>
// 2) Initialize GTM after defaults and after reading saved preferences (hydrate early).
//    This page replays stored choices via applyConsentModeFromPreferences on mount.
// 3) Consider persisting a server-side record of consent (hashed user/session) for audit.
